/**
 * Theme Module
 *
 * Manages dark/light/auto theme preference via localStorage.
 * Toggles the .dark-theme class on <html> to activate dark mode CSS variables.
 */

const STORAGE_KEY = 'vmsman-theme';

export type Theme = 'auto' | 'light' | 'dark';

const VALID_THEMES: Theme[] = ['auto', 'light', 'dark'];

let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

function setDarkClass(dark: boolean): void {
  document.documentElement.classList.toggle('dark-theme', dark);
}

function removeMediaListener(): void {
  if (mediaListener) {
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', mediaListener);
    mediaListener = null;
  }
}

/**
 * Read stored theme preference (defaults to 'auto')
 */
export function getStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && VALID_THEMES.includes(stored as Theme)) return stored as Theme;
  return 'auto';
}

/**
 * Apply the given theme (or the stored one if omitted).
 * Persists to localStorage and updates the DOM immediately.
 */
export function applyTheme(theme?: Theme): void {
  const t = theme ?? getStoredTheme();
  localStorage.setItem(STORAGE_KEY, t);

  removeMediaListener();

  if (t === 'dark') {
    setDarkClass(true);
  } else if (t === 'light') {
    setDarkClass(false);
  } else {
    // Auto: follow OS preference
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setDarkClass(mq.matches);
    mediaListener = (e) => setDarkClass(e.matches);
    mq.addEventListener('change', mediaListener);
  }
}
